import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Polyline, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { getSummaryReport, getTripsReport, getStopsReport, getEventsReport, getRouteReport } from './traccar';
import { alertsForEntityOnDay } from './alertLog';
import { idleForDay } from './idleStore';

const knMph = (kn) => Math.round((kn || 0) * 1.15078);
const mMi = (m) => +((m || 0) / 1609.344).toFixed(2);
const fmtDur = (ms) => {
  const s = Math.round((ms || 0) / 1000);
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return h > 0 ? `${h}h ${m}m ${sec}s` : m > 0 ? `${m}m ${sec}s` : `${sec}s`;
};
const haversineMi = (a, b) => {
  const R = 3958.8, dLat = (b[0] - a[0]) * Math.PI / 180, dLng = (b[1] - a[1]) * Math.PI / 180;
  const la1 = a[0] * Math.PI / 180, la2 = b[0] * Math.PI / 180;
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
  return +(2 * R * Math.asin(Math.sqrt(x))).toFixed(2);
};
const dayKey = (d) => d.toLocaleDateString('en-CA');
const tag = (label, color) => L.divIcon({ className: '', iconSize: [34, 20], iconAnchor: [17, 10], html: `<div style="background:${color};color:#fff;font-weight:700;font-size:11px;padding:2px 6px;border-radius:4px;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.5)">${label}</div>` });

// notable event types to pin on the map (skip routine move/stop/online noise)
const EVENT_META = {
  alarm: { emoji: '🚨', label: 'Alarm' },
  deviceOverspeed: { emoji: '⏩', label: 'Overspeed' },
  ignitionOn: { emoji: '🔑', label: 'Ignition on' },
  ignitionOff: { emoji: '🔒', label: 'Ignition off' },
  geofenceEnter: { emoji: '📍', label: 'Entered zone' },
  geofenceExit: { emoji: '📤', label: 'Left zone' },
  maintenance: { emoji: '🔧', label: 'Maintenance' },
};
const ALARM_LABEL = { hardBraking: 'Hard braking', hardAcceleration: 'Harsh acceleration', hardCornering: 'Harsh cornering', accident: 'Crash', towing: 'Tow', overspeed: 'Overspeed', idle: 'Idling' };
const eventIcon = (emoji) => L.divIcon({ className: '', iconSize: [22, 22], iconAnchor: [11, 11], html: `<div style="width:22px;height:22px;border-radius:50%;background:#fff;border:2px solid #dc2626;display:flex;align-items:center;justify-content:center;font-size:12px;box-shadow:0 1px 4px rgba(0,0,0,.5)">${emoji}</div>` });

const Row = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', padding: '7px 4px', borderBottom: HAIR, fontSize: '13px' }}>
    <span style={{ color: TXT_MUTED }}>{label}</span>
    <span style={{ fontWeight: 700, color: TXT }}>{value}</span>
  </div>
);

// native app = dark glass skin
const IS_NATIVE = typeof window !== 'undefined' && !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
const TXT = IS_NATIVE ? '#e8ebf5' : '#0f172a';
const TXT_MUTED = IS_NATIVE ? '#9aa0b4' : '#64748b';
const PANEL = IS_NATIVE ? '#0a0f1a' : 'white';
const HAIR = IS_NATIVE ? '1px solid rgba(255,255,255,0.10)' : '1px solid #f1f5f9';
const hhmm = (t) => (t ? new Date(t).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '—');

export default function VehicleHistory({ device, devices = [], onClose, asPage = false }) {
  const [dev, setDev] = useState(device); // active car (switchable in-screen)
  const [day, setDay] = useState(new Date());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [carMenu, setCarMenu] = useState(false);
  const stripRef = useRef(null);
  useEffect(() => { setDev(device); }, [device]); // reopened for a different car
  const dName = (d) => (d ? ((d.attributes || {}).displayName || d.name) : '');
  const pickList = (devices && devices.length ? devices : [device]).filter(Boolean);

  // oldest → newest so today sits on the RIGHT (matches the reference app)
  const days = Array.from({ length: 14 }, (_, i) => { const d = new Date(); d.setDate(d.getDate() - (13 - i)); return d; });

  useEffect(() => { if (stripRef.current) stripRef.current.scrollLeft = stripRef.current.scrollWidth; }, []);

  // ---- estimated tolls -------------------------------------------------
  // There's no toll data in the tracker feed, so we infer it: sample the day's
  // route, ask OpenStreetMap which of those points sit on a road tagged
  // toll=yes, and count the distinct toll roads travelled. It's an ESTIMATE —
  // it counts toll roads used, not individual plazas or actual charges.
  const [tolls, setTolls] = useState(null); // { roads:[names], est:number }
  useEffect(() => {
    let cancelled = false;
    setTolls(null);
    const pts = (data && data.route) || [];
    if (pts.length < 2) return undefined;
    // sample up to 24 points evenly across the day so one query covers the trip
    const step = Math.max(1, Math.floor(pts.length / 24));
    const sample = pts.filter((_, i) => i % step === 0).slice(0, 24);
    const clauses = sample.map((p) => `way(around:25,${p.latitude},${p.longitude})[highway][toll=yes];`).join('');
    const q = `[out:json][timeout:20];(${clauses});out tags;`;
    (async () => {
      try {
        const r = await fetch('https://overpass-api.de/api/interpreter', {
          method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: 'data=' + encodeURIComponent(q),
        });
        if (!r.ok) return;
        const j = await r.json();
        if (cancelled) return;
        const names = new Set();
        for (const el of (j.elements || [])) {
          const tg = el.tags || {};
          const nm = tg.name || tg.ref;
          if (nm) names.add(String(nm));
        }
        const roads = [...names];
        setTolls({ roads, est: roads.length * 1.75 }); // ~$1.75 average per toll road used
      } catch { /* leave null — we just won't show an estimate */ }
    })();
    return () => { cancelled = true; };
  }, [data]);

  useEffect(() => {
    if (!dev) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true); setError(null);
      const y = day.getFullYear(), mo = day.getMonth(), da = day.getDate();
      const from = new Date(y, mo, da, 0, 0, 0).toISOString();
      const to = new Date(y, mo, da, 23, 59, 59).toISOString();
      try {
        const [summary, trips, stops, events, route] = await Promise.all([
          getSummaryReport(dev.id, from, to).catch(() => null),
          getTripsReport(dev.id, from, to).catch(() => []),
          getStopsReport(dev.id, from, to).catch(() => []),
          getEventsReport(dev.id, from, to).catch(() => []),
          getRouteReport(dev.id, from, to).catch(() => []),
        ]);
        if (cancelled) return;
        setData({ summary, trips, stops, events, route });
      } catch (e) { if (!cancelled) setError(e.message); }
      if (!cancelled) setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [dev, day]);

  if (!dev) return null;

  const s = data && data.summary;
  const route = (data && data.route) || [];
  // Drop junk fixes: missing/NaN coords, out-of-range, and the classic 0,0
  // "null island" point (lands in the Atlantic off Africa) that a tracker emits
  // when it has no satellite lock. Left in, one 0,0 dragged the route into the
  // ocean and blew up the straight-line distance.
  const validPt = (lat, lng) =>
    Number.isFinite(lat) && Number.isFinite(lng) &&
    Math.abs(lat) <= 90 && Math.abs(lng) <= 180 &&
    !(Math.abs(lat) < 0.001 && Math.abs(lng) < 0.001);
  const path = route.filter((p) => validPt(p.latitude, p.longitude)).map((p) => [p.latitude, p.longitude]);
  const timeInMotion = (data && data.trips || []).reduce((a, t) => a + (t.duration || 0), 0);
  const timeParked = (data && data.stops || []).reduce((a, t) => a + (t.duration || 0), 0);
  const alertInfo = alertsForEntityOnDay(dev.name, dayKey(day));
  const idleSec = idleForDay(dev.id, day.toDateString(), new Date().toDateString());
  const straight = path.length > 1 ? haversineMi(path[0], path[path.length - 1]) : 0;

  // extras an owner actually cares about: when the car first moved / last stopped,
  // the longest single trip, places visited, and a day driver score.
  const tripList = (data && data.trips) || [];
  const stopList = (data && data.stops) || [];
  const evList = (data && data.events) || [];
  const firstDrive = tripList.length ? tripList[0].startTime : null;
  const lastDrive = tripList.length ? tripList[tripList.length - 1].endTime : null;
  const longestTrip = tripList.reduce((best, t) => (!best || (t.distance || 0) > (best.distance || 0) ? t : best), null);
  const placesVisited = new Set(evList.filter((e) => e.type === 'geofenceEnter').map((e) => e.geofenceId)).size;

  // Idle = engine running while not moving. Prefer the tracker's own numbers:
  // engine-hours logged during stops, else engine hours minus driving time, and
  // only fall back to the dashboard's live tally (which is 0 for past days).
  const idleFromStops = stopList.reduce((a, st) => a + (st.engineHours || 0), 0);
  const idleDerived = s && s.engineHours ? Math.max(0, s.engineHours - timeInMotion) : 0;
  const idleMs = idleFromStops > 0 ? idleFromStops : (idleDerived > 0 ? idleDerived : idleSec * 1000);

  // count speeding separately from harsh braking/accel — Traccar reports these
  // either as an event type or as an `alarm` attribute, so check both.
  const evKind = (e) => `${e.type || ''} ${(e.attributes || {}).alarm || ''}`;
  const speedingCount = evList.filter((e) => /overspeed|speeding/i.test(evKind(e))).length;
  const harshCount = evList.filter((e) => /hardBraking|hardAcceleration|hardCornering|accident/i.test(evKind(e))).length;
  const harsh = speedingCount + harshCount;
  const milesDay = s ? mMi(s.distance) : 0;

  // tracker battery across the day (last reading + the low-water mark), so the
  // owner can see if the unit is losing charge.
  const battPct = (p) => {
    const pa = (p && p.attributes) || {};
    if (pa.batteryLevel != null && pa.batteryLevel > 0) return Math.round(pa.batteryLevel);
    if (pa.battery != null && pa.battery >= 3.0) return Math.round(Math.min(100, Math.max(0, ((pa.battery - 3.5) / (4.2 - 3.5)) * 100)));
    return null;
  };
  const battSeries = route.map(battPct).filter((n) => n != null);
  const battLast = battSeries.length ? battSeries[battSeries.length - 1] : null;
  const battMin = battSeries.length ? Math.min(...battSeries) : null;
  const battVolts = (() => {
    for (let i = route.length - 1; i >= 0; i -= 1) {
      const bv = (route[i].attributes || {}).battery;
      if (bv != null && bv > 0) return Number(bv).toFixed(2);
    }
    return null;
  })();
  // speeding is weighted heavier than a single hard brake
  const penalty = milesDay > 0 ? ((speedingCount * 1.5 + harshCount) / milesDay) * 100 * 4 : (speedingCount * 3 + harshCount * 2);
  const dayScore = Math.max(40, Math.min(100, Math.round(100 - penalty)));
  const scoreColor = dayScore >= 85 ? '#22c55e' : dayScore >= 70 ? '#fbbf24' : '#f87171';

  // pin notable events on the route
  const posById = {};
  route.forEach((p) => { posById[p.id] = p; });
  const eventMarkers = ((data && data.events) || []).map((ev, i) => {
    const meta = EVENT_META[ev.type];
    if (!meta) return null;
    const p = posById[ev.positionId];
    if (!p || !validPt(p.latitude, p.longitude)) return null;
    const alarm = (ev.attributes || {}).alarm;
    const label = ev.type === 'alarm' ? (ALARM_LABEL[alarm] || 'Alarm') : meta.label;
    return { key: ev.id || i, lat: p.latitude, lng: p.longitude, emoji: meta.emoji, label, time: ev.eventTime };
  }).filter(Boolean);

  // asPage (from the nav tab) → render in normal flow so the top banner/tabs stay
  // visible and the customer can scroll to any other tab. Otherwise it's a
  // full-screen modal opened over the map (from the Trip Map button).
  const outerStyle = asPage
    ? { background: PANEL, minHeight: '100%' }
    : { position: 'fixed', inset: 0, background: PANEL, zIndex: 1200, display: 'flex', flexDirection: 'column', overflowY: 'auto', WebkitOverflowScrolling: 'touch' };
  // Full-screen modal covers the status bar, so the header (and its × button)
  // must clear it. On some WebViews env(safe-area-inset-top) resolves to 0,
  // which put the × up under the clock/battery where it couldn't be tapped —
  // so floor the inset at 46px to guarantee the button sits below the status bar.
  const headerTopPad = asPage ? '14px' : 'calc(max(env(safe-area-inset-top, 0px), 46px) + 14px)';
  return (
    <div style={outerStyle}>
      <div style={{ background: PANEL, width: '100%', maxWidth: '760px', boxSizing: 'border-box', margin: '0 auto', paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 24px)' }}>
        {/* header */}
        <div style={{ position: 'sticky', top: 0, zIndex: 2, background: PANEL, display: 'flex', alignItems: 'center', gap: '12px', padding: `${headerTopPad} 20px 14px`, borderBottom: HAIR }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <button onClick={() => setCarMenu((v) => !v)} style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', maxWidth: '100%' }}>
              <h2 style={{ margin: 0, fontFamily: 'Poppins, sans-serif', fontSize: '18px', textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>🗓️ {dName(dev)}</h2>
              {pickList.length > 1 && <span style={{ color: '#94a3b8', fontSize: '13px' }}>{carMenu ? '▲' : '▼'}</span>}
            </button>
            <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Trip History</div>
            {carMenu && pickList.length > 1 && (
              <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: '6px', background: PANEL, border: HAIR, borderRadius: '12px', boxShadow: '0 12px 30px rgba(0,0,0,0.35)', zIndex: 5, minWidth: '220px', overflow: 'hidden' }}>
                {pickList.map((d) => (
                  <button key={d.id} onClick={() => { setDev(d); setCarMenu(false); }} style={{ display: 'block', width: '100%', textAlign: 'left', background: String(d.id) === String(dev.id) ? (IS_NATIVE ? 'rgba(255,255,255,0.10)' : '#eff6ff') : PANEL, border: 'none', borderBottom: HAIR, padding: '11px 14px', fontSize: '14px', fontWeight: 700, color: TXT, cursor: 'pointer' }}>
                    {String(d.id) === String(dev.id) ? '✓ ' : ''}{dName(d)}
                  </button>
                ))}
              </div>
            )}
          </div>
          {!asPage && <button onClick={onClose} aria-label="Close" style={{ background: '#f1f5f9', border: 'none', borderRadius: '50%', width: '38px', height: '38px', padding: 0, fontSize: '20px', lineHeight: 1, color: '#475569', flexShrink: 0, cursor: 'pointer', alignSelf: 'flex-start' }}>×</button>}
        </div>

        {/* day selector */}
        <div ref={stripRef} style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', overflowX: 'auto', overflowY: 'hidden', padding: '12px 16px', background: IS_NATIVE ? 'rgba(255,255,255,0.04)' : '#f8fafc' }}>
          {days.map((d) => {
            const sel = dayKey(d) === dayKey(day);
            return (
              <button key={dayKey(d)} onClick={() => setDay(d)} style={{
                flexShrink: 0, width: '54px', height: '52px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '6px 0', borderRadius: '10px', textAlign: 'center', cursor: 'pointer',
                background: sel ? 'linear-gradient(135deg, #dc2626, #991b1b)' : (IS_NATIVE ? 'rgba(255,255,255,0.07)' : 'white'), color: sel ? 'white' : (IS_NATIVE ? TXT : '#334155'),
                border: '1px solid ' + (sel ? 'transparent' : (IS_NATIVE ? 'rgba(255,255,255,0.12)' : '#e2e8f0')),
              }}>
                <div style={{ fontSize: '10px', textTransform: 'uppercase', opacity: 0.8 }}>{d.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                <div style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: '16px' }}>{d.getDate()}</div>
              </button>
            );
          })}
        </div>

        {/* map */}
        <div style={{ height: 'clamp(300px, 44vh, 480px)', background: '#e5e7eb' }}>
          {path.length > 0 ? (
            <MapContainer bounds={path.length > 1 ? path : undefined} center={path[0]} zoom={12} style={{ height: '100%', width: '100%' }}>
              <TileLayer className="sat-bright" url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" />
              <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}" />
              {/* white casing + bright line so the route stands out on any map */}
              <Polyline positions={path} pathOptions={{ color: '#ffffff', weight: 8, opacity: 0.9 }} />
              <Polyline positions={path} pathOptions={{ color: '#7c3aed', weight: 4, opacity: 1 }} />
              <Marker position={path[0]} icon={tag('INI', '#16a34a')} />
              <Marker position={path[path.length - 1]} icon={tag('FIN', '#dc2626')} />
              {eventMarkers.map((m) => (
                <Marker key={m.key} position={[m.lat, m.lng]} icon={eventIcon(m.emoji)}>
                  <Popup>
                    <div style={{ fontSize: '13px' }}>
                      <div style={{ fontWeight: 700, marginBottom: '2px' }}>{m.emoji} {m.label}</div>
                      <div style={{ color: '#64748b', marginBottom: '6px' }}>{new Date(m.time).toLocaleString()}</div>
                      <a href={`https://www.google.com/maps?q=&layer=c&cbll=${m.lat},${m.lng}`} target="_blank" rel="noreferrer" style={{ color: '#1d4ed8', fontWeight: 700 }}>🧍 Open Street View</a>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          ) : (
            <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '20px', color: '#94a3b8', fontSize: '14px' }}>
              {loading ? 'Loading route…'
                : route.length > 0
                  // The tracker DID report this day, but every fix was 0,0 (no GPS
                  // lock) so there's no real route to draw. That's a GPS/antenna
                  // issue, not a disconnect — the device was online and logging.
                  ? '📡 The tracker was reporting this day but never got a GPS satellite lock, so there is no map route. The trip distance below comes from the odometer. Check the GPS antenna/mounting.'
                  : 'No route recorded for this day'}
            </div>
          )}
        </div>

        {/* metrics */}
        <div style={{ padding: '16px 20px' }}>
          {error && <p style={{ color: '#dc2626' }}>🔴 {error}</p>}
          {/* day summary: driver score + the headline numbers */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px', marginBottom: '14px', borderRadius: '18px', background: IS_NATIVE ? 'linear-gradient(150deg, rgba(255,255,255,0.09), rgba(255,255,255,0.03))' : '#f8fafc', border: IS_NATIVE ? '1px solid rgba(255,255,255,0.10)' : '1px solid #e2e8f0' }}>
            <div style={{ width: '66px', height: '66px', borderRadius: '50%', flexShrink: 0, background: `conic-gradient(${scoreColor} 0% ${dayScore}%, ${IS_NATIVE ? 'rgba(255,255,255,0.10)' : '#e2e8f0'} ${dayScore}% 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ width: '52px', height: '52px', borderRadius: '50%', background: PANEL, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Poppins, sans-serif', fontWeight: 800, fontSize: '19px', color: TXT }}>{dayScore}</span>
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: TXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Driver score</div>
              <div style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: '15px', color: TXT, marginTop: '2px' }}>
                {milesDay} mi · {tripList.length} trip{tripList.length === 1 ? '' : 's'}
              </div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '5px' }}>
                {harsh === 0 && <span style={{ fontSize: '11px', fontWeight: 700, color: '#4ade80', background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: '999px', padding: '2px 9px' }}>Clean driving</span>}
                {speedingCount > 0 && <span style={{ fontSize: '11px', fontWeight: 700, color: '#f87171', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '999px', padding: '2px 9px' }}>⏩ {speedingCount} speeding</span>}
                {harshCount > 0 && <span style={{ fontSize: '11px', fontWeight: 700, color: '#fbbf24', background: 'rgba(251,191,36,0.15)', border: '1px solid rgba(251,191,36,0.3)', borderRadius: '999px', padding: '2px 9px' }}>⚠️ {harshCount} harsh</span>}
              </div>
            </div>
          </div>
          <div className="grid grid-2" style={{ gap: '0 28px' }}>
            <div>
              <div style={{ fontSize: '12px', fontWeight: 700, color: '#0066ff', marginBottom: '4px' }}>TOUR DETAIL</div>
              <Row label="Distance (GPS)" value={`${s ? mMi(s.distance) : 0} mi`} />
              <Row label="Distance straight" value={`${straight} mi`} />
              <Row label="Max speed" value={`${s ? knMph(s.maxSpeed) : 0} mph`} />
              <Row label="Average speed" value={`${s ? knMph(s.averageSpeed) : 0} mph`} />
              <Row label="Initial odometer" value={s && s.startOdometer ? `${mMi(s.startOdometer).toLocaleString()} mi` : '—'} />
              <Row label="Final odometer" value={s && s.endOdometer ? `${mMi(s.endOdometer).toLocaleString()} mi` : '—'} />
              <Row label="Engine hours" value={s && s.engineHours ? fmtDur(s.engineHours) : '—'} />
              <Row label="Tracker battery" value={battLast != null ? `${battLast}%${battVolts ? ` · ${battVolts}V` : ''}` : (battVolts ? `${battVolts}V` : '—')} />
              <Row label="Battery low point" value={battMin != null ? `${battMin}%` : '—'} />
              <Row label="Fuel used" value={s && s.spentFuel ? `${s.spentFuel.toFixed(1)} L` : '—'} />
            </div>
            <div>
              <div style={{ fontSize: '12px', fontWeight: 700, color: '#0066ff', marginBottom: '4px' }}>TRAVEL DETAILS</div>
              <Row label="Time in motion" value={fmtDur(timeInMotion)} />
              <Row label="Time parked" value={fmtDur(timeParked)} />
              <Row label="Idle time" value={fmtDur(idleMs)} />
              <Row label="Speeding events" value={speedingCount} />
              <Row label="Harsh braking / accel" value={harshCount} />
              <Row label="Trips" value={(data && data.trips || []).length} />
              <Row label="Stops" value={(data && data.stops || []).length} />
              <Row label="First drive" value={hhmm(firstDrive)} />
              <Row label="Last drive" value={hhmm(lastDrive)} />
              <Row label="Longest trip" value={longestTrip ? `${mMi(longestTrip.distance)} mi` : '—'} />
              <Row label="Places visited" value={placesVisited || '—'} />
              <Row label="Estimated tolls" value={tolls ? (tolls.roads.length ? `~$${tolls.est.toFixed(2)} · ${tolls.roads.length} toll road${tolls.roads.length === 1 ? '' : 's'}` : 'None detected') : 'Checking…'} />
              <Row label="Events" value={(data && data.events || []).length} />
              <Row label="Alerts" value={alertInfo.count} />
            </div>
          </div>
          {alertInfo.count > 0 && (
            <div style={{ marginTop: '12px' }}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: TXT_MUTED, marginBottom: '4px' }}>ALERTS THIS DAY</div>
              {Object.entries(alertInfo.byType).map(([t, n]) => (
                <span key={t} style={{ display: 'inline-block', margin: '2px 6px 2px 0', fontSize: '12px', padding: '2px 10px', borderRadius: '999px', background: '#fef2f2', color: '#b91c1c', fontWeight: 600 }}>{t} × {n}</span>
              ))}
            </div>
          )}
          <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '12px' }}>Driving metrics and route come from the tracker's stored history; alerts/idle are tallied by the dashboard.</p>
        </div>
      </div>
    </div>
  );
}
